<template>
  <div>
    <div class="form-horizontal columns">
      <div class="form-group column col-6">
        <div class="col-4 col-sm-12">
          <label class="form-label" for="search-user">Lookup AO3 user</label>
        </div>

        <div class="col-8 col-sm-12 input-group">
          <input class="form-input" type="text" id="search-user" placeholder="AO3 username" v-model="user"
            @keyup.enter="getUserPrompts(user)" />
          <button class="btn-secondary btn search-user-submit input-group-btn" @click="getUserPrompts(user)"
            @keyup.enter="getUserPrompts(user)">
            Go!
          </button>
        </div>
      </div>
    </div>

    <h2 v-if="dedupedPrompts.length">{{ dedupedPrompts[0].username }}</h2>

    <div>
      <label class="form-switch form-inline">
        <input type="checkbox" v-model="options.useToggles" />
        <i class="form-icon"></i> Use toggles for optional details
      </label>
      <label class="form-switch form-inline">
        <input type="checkbox" v-model="options.truncateTags" />
        <i class="form-icon"></i> Use toggles for optional/freeform tags
      </label>
      <label v-if="options.truncateTags" class="max-tags-label form-inline form-label mr-2" for="fandom-filter">after
        <input class="form-input form-inline max-tags" id="max-tags" type="number" placeholder="10" min="0"
          v-model="options.tagLimit">
        tags
      </label>
      <label class="form-switch form-inline">
        <input type="checkbox" v-model="options.openDetails" />
        <i class="form-icon"></i> Auto-expand details
        <span class="tooltip c-hand" data-tooltip="Opens HTML details elements">
          <sup>
            <i class="fa fa-question-circle" aria-hidden="true"></i>
          </sup>
        </span>
      </label>
    </div>

    <template v-if="!loading && dedupedPrompts.length">
      <div v-if="letter">
        <a :href="formatUrl(letter)" target="blank">Letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
          <sup>
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>
      </div>

      <table v-table-resizer v-if="dedupedPrompts.length">
        <thead>
          <tr>
            <th v-if="!isExchangeOpen" class="bookmark fave">
              <span class="fas fa-heart"></span>
            </th>
            <th class="fandom">Fandom</th>
            <th class="characters">Characters & Relationships</th>
            <th>Prompt</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(prompt, index) in dedupedPrompts" :key="index"
            :class="{ odd: index % 2 === 1, even: index % 2 == 0 }">
            <td v-if="!isExchangeOpen">
              <button aria-label="Toggle prompt bookmark" class="bookmark" @click="togglePromptmark(prompt)">
                <span v-if="hasPromptmark(prompt)" aria-label="Remove bookmark" class="fas fa-heart"></span>
                <span v-else class="far fa-heart" aria-label="Bookmark"></span>
              </button>
            </td>
            <td>{{ prompt.fandom }}</td>

            <td>
              <div class="requested-characters" v-if="prompt.relationships || prompt.characters">
                <character-list :characters="prompt.characters" :relationships="prompt.relationships"
                  :hideTitle="true"></character-list>
              </div>
              <div v-else>None</div>
            </td>
            <td>
              <chips v-if="prompt.fandoms && prompt.fandoms.length > 1"
                :tags="prompt.fandoms.filter(f => f != prompt.fandom)" type="fandom" :exchangeId="exchangeId"
                title="Other Fandoms"></chips>
              <div class="mb-2" v-if="prompt.title">
                <strong>Title:</strong>&nbsp;<span>{{ prompt.title }}</span>
              </div>


              <chips :tags="prompt.warnings" title="Warnings"></chips>
              <chips :tags="prompt.ratings" title="Ratings"></chips>

              <chips :tags="prompt.freeforms" title="Freeforms" :truncate="options.truncateTags"
                :limit="options.tagLimit"></chips>

              <chips :tags="prompt['optional_tags']" title="Optional Tags" :truncate="options.truncateTags"
                :limit="options.tagLimit"></chips>

              <div class="mb-2" v-if="prompt.letter && prompt.letter.length">
                <a :href="prompt.letter" target="blank">Letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                  <sup>
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>
              </div>

              <template v-if="(!options.useToggles || shouldShowPrompt(prompt.sha)) &&
                prompt.prompt
              ">
                <div v-html="prompt.prompt" class="prompt-text mt-2"></div>
              </template>
              <button class="btn btn-sm" v-if="options.useToggles && prompt.prompt" @click="togglePrompt(prompt.sha)">
                <span v-if="shouldShowPrompt(prompt.sha)">Show less
                </span>
                <span v-else>Read optional details
                </span>
              </button>

              <p v-else-if="!prompt.prompt">
                No optional details given.
                <span v-if="prompt.letter">
                  However, they do have a
                  <a :href="formatUrl(prompt.letter)" target="blank">letter </a><span class="tooltip c-hand"
                    data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                    <sup>
                      <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <div v-else-if="notFound">
      User not found! Search is case-sensitive and preserves whitespace - maybe
      check your search and try again?
    </div>
    <div v-if="loading" class="loading"></div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { includes, remove, uniqWith, map, reverse } from "lodash-es"
import chips from "../components/chips.vue";
import characterList from "../components/character-list.vue";
import tableResizer from "../directives/tableResizer";
import utils from "../components/utils.js";
import axios from "axios";
export default {
  directives: {
    tableResizer
  },
  components: { chips, characterList },
  computed: {
    ...mapGetters(["exchangeId", "isExchangeOpen", "promptmarks", "options"]),
    dedupedPrompts() {

      let arr = map(this.prompts, (req, i) => {
        req.request_id = req.request_id ? req.request_id : i;
        return req;
      })

      return reverse(uniqWith(reverse(arr), (a, b) => {
        return a.request_id == b.request_id
      }))
    }
  },
  data() {
    return {
      user: "",
      letter: "",
      prompts: [],
      loading: true,
      notFound: false,
      showPrompt: []
    };
  },

  beforeMount() {
    this.user = this.$route.params.id;
    if (!this.exchangeId) {
      this.$store.commit("setExchangeId", this.$route.params.exchangeId);
    }
    this.getData();
  },
  updated() {
    utils.openDetails();

  },
  watch: {
    $route(to) {
      if (to.name.indexOf("user") > -1) {
        this.user = this.$route.params.id;
        this.getData();
      }
    },
    options: {
      handler(val) {
        if (val.openDetails) {
          utils.openDetails();
        }
        this.$store.commit("setOptions", val);
        this.$localStorage.set("appOptions", JSON.stringify(val));
      },
      deep: true
    }
  },
  methods: {
    ...utils,
    async getData() {
      if (!this.user || !this.user.length) {
        this.loading = false;
        return;
      }

      await this.redirectExchangeId();


      axios
        .get(
          `https://autoao3.herokuapp.com/db/exchanges/${this.exchangeId}/status`
        )
        .then(res => {
          this.$store.commit(
            "setIsExchangeOpen",
            res.data && (res.data.status == "open" || res.data.status == "processing")
          );
        });

      axios
        .get(
          `https://autoao3.herokuapp.com/db/exchanges/${this.exchangeId}/users/${this.user}`
        )
        .then(res => {
          if (res.data && res.data.length) {
            if (this.exchangeId && this.exchangeId.toLowerCase() == "auctionsasthreatened") {
              axios
                .get(
                  `https://autoao3.herokuapp.com/db/exchanges/AAT_Request_Overflow/users/${this.user}`
                ).then(res2 => {
                  if (res2.data && res2.data.length) {
                    this.prompts = res.data.concat(res2.data);
                    this.letter = this.prompts[0].letter;
                  } else {
                    this.prompts = res.data;
                    this.letter = this.prompts[0].letter;
                  }
                })
            } else if (this.exchangeId && this.exchangeId.toLowerCase() == "aat_request_overflow") {
              axios
                .get(
                  `https://autoao3.herokuapp.com/db/exchanges/AuctionsAsThreatened/users/${this.user}`
                ).then(res2 => {
                  if (res2.data && res2.data.length) {
                    this.prompts = res.data.concat(res2.data);
                    this.letter = this.prompts[0].letter;
                  } else {
                    this.prompts = res.data;
                    this.letter = this.prompts[0].letter;
                  }
                })
            } else {
              this.prompts = res.data;
              this.letter = this.prompts[0].letter;
            }
          } else {
            this.prompts = [];
            this.notFound = true;
          }
          this.loading = false;
        });
    },
    getUserPrompts() {
      this.notFound = false;

      if (!this.user || !this.user.length) {
        this.loading = false;
        return;
      }

      if (this.$route.path !== `/${this.exchangeId}/user/${this.user}`) {
        this.$router.push({ path: `/${this.exchangeId}/user/${this.user}` });
      }
    },
    togglePrompt(sha) {
      if (this.shouldShowPrompt(sha)) {
        this.showPrompt = remove(this.showPrompt, sha);
      } else {
        this.showPrompt.push(sha);
      }
    },
    shouldShowPrompt(sha) {
      return includes(this.showPrompt, sha);
    },
  }
};
</script>

<style lang="scss" scoped>
th.bookmark {
  width: 25px;
  color: black;

  span {
    cursor: initial !important;
  }
}

th.characters {
  width: 200px;
}


.max-tags-label {
  margin-left: -0.2rem;
}

.max-tags {
  height: 1.2rem;
  line-height: 1.2rem;
  padding: 3px;
  width: 40px;
  margin-left: 0.25rem;
}
</style>
